import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Landmark, Pencil, Plus, Trash2 } from 'lucide-react';
import { DashboardLayout } from '../common/page_component/index.js';
import ListPageHeader from '../common/ListPageHeader.js';
import Button from '../common/Button.js';
import Loader from '../common/Loader.js';
import Alert from '../common/Alert.js';
import plStatutoryService from '../../services/plStatutoryService.js';

const money = (v) =>
  `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const EXP_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'fixed', label: 'Fixed' },
  { value: 'variable', label: 'Variable' },
  { value: 'operational', label: 'Operational' },
];

const DUE_DAY_OPTIONS = [
  'DAILY',
  '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th',
  '11th', '12th', '13th', '14th', '15th', '16th', '17th', '18th', '19th', '20th',
  '21st', '22nd', '23rd', '24th', '25th', '26th', '27th', '28th', '29th', '30th', '31st',
];

const emptyForm = {
  name: '',
  exp_type: 'fixed',
  monthly_amount: '',
  payment_due_day: '1st',
  opening_balance: '',
  is_active: true,
};

const daysInThisMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate() || 30;
};

const todayIso = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const expLabel = (row) => {
  const raw = String(row.exp_type || row.item_type || '').toLowerCase();
  if (raw === 'admin') return 'Admin';
  if (raw === 'variable') return 'Variable';
  if (raw === 'operational') return 'Operational';
  if (raw === 'overhead') return 'Fixed';
  return 'Fixed';
};

const PlStatutoryPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const days = daysInThisMonth();

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const res = await plStatutoryService.list(params);
      setItems(res?.data || []);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to load payable items');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const totals = useMemo(() => {
    const active = items.filter((row) => row.is_active);
    const admin = active
      .filter((row) => String(row.exp_type || row.item_type).toLowerCase() === 'admin')
      .reduce((sum, row) => sum + Number(row.monthly_amount || 0), 0);
    const overhead = active
      .filter((row) => String(row.exp_type || row.item_type).toLowerCase() !== 'admin')
      .reduce((sum, row) => sum + Number(row.monthly_amount || 0), 0);
    return {
      admin,
      overhead,
      adminDay: admin / days,
      overheadDay: overhead / days,
    };
  }, [items, days]);

  const startCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name || '',
      exp_type: row.exp_type || (row.item_type === 'admin' ? 'admin' : 'fixed'),
      monthly_amount: row.monthly_amount != null ? String(row.monthly_amount) : '',
      payment_due_day: row.payment_due_day || '1st',
      opening_balance: row.opening_balance != null ? String(row.opening_balance) : '',
      is_active: Boolean(row.is_active),
    });
  };

  const saveItem = async (event) => {
    event.preventDefault();
    const name = form.name.trim();
    const amount = Number(form.monthly_amount);
    const opening = Number(form.opening_balance || 0);
    if (!name) {
      Alert.error('Enter a name, for example Godown rent');
      return;
    }
    if (!Number.isFinite(amount) || amount < 0) {
      Alert.error('Enter a valid monthly amount');
      return;
    }
    if (!Number.isFinite(opening) || opening < 0) {
      Alert.error('Enter a valid previous O/S (opening balance)');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        name,
        exp_type: form.exp_type,
        monthly_amount: amount,
        payment_due_day: form.payment_due_day,
        opening_balance: opening,
        is_active: form.is_active,
      };
      if (editingId) {
        await plStatutoryService.update(editingId, payload);
        Alert.success('Payable item updated');
      } else {
        await plStatutoryService.create(payload);
        Alert.success('Payable item added');
      }
      setForm(emptyForm);
      setEditingId(null);
      await loadItems();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Could not save item');
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (row) => {
    const ok = await Alert.confirm(
      `Remove ${row.name}? Daily P&L and Payables Statement will stop using this amount.`,
      'Remove payable item',
    );
    if (!ok) return;
    try {
      await plStatutoryService.remove(row.id);
      if (editingId === row.id) {
        setEditingId(null);
        setForm(emptyForm);
      }
      Alert.success('Item removed');
      await loadItems();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Could not remove item');
    }
  };

  const togglePayment = async (row) => {
    try {
      setTogglingId(row.id);
      await plStatutoryService.togglePayment(row.id, {
        payment_date: todayIso(),
      });
      await loadItems();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Could not update payment status');
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-3 sm:p-6">
        <ListPageHeader
          title="Payable"
          icon={Landmark}
          subtitle="Monthly rent, EMI, salaries and overhead. Daily P&L splits these by days in the month. Use MIS → Payable Report for the Business Payables Statement."
        >
          <Button variant="secondary" icon={Plus} onClick={startCreate}>
            New item
          </Button>
        </ListPageHeader>

        <p className="text-xs text-gray-500 mb-4 max-w-3xl">
          This is not Expense Management. Trip hire, forwarding and delivery stay on Daily P&L Desk.
          Put repeating costs here (godown rent, vehicle EMI, office rent, branch commission).
          Final O/S on the Payable Report = Previous O/S + Today&apos;s expense − Payment Made.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-3 mb-4">
          <div className="bg-white border border-gray-200 rounded-xl p-3">
            <p className="text-[10px] uppercase text-gray-500">Overhead / month</p>
            <p className="text-lg font-bold text-gray-800">{money(totals.overhead)}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-3">
            <p className="text-[10px] uppercase text-gray-500">Overhead / day</p>
            <p className="text-lg font-bold text-gray-800">{money(totals.overheadDay)}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-3">
            <p className="text-[10px] uppercase text-gray-500">Admin expenses / month</p>
            <p className="text-lg font-bold text-gray-800">{money(totals.admin)}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-3">
            <p className="text-[10px] uppercase text-gray-500">Admin expenses / day</p>
            <p className="text-lg font-bold text-gray-800">{money(totals.adminDay)}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-3">
            <p className="text-[10px] uppercase text-gray-500">Days this month</p>
            <p className="text-lg font-bold text-gray-800">{days}</p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-3 sm:p-4 mb-4 flex flex-wrap gap-3 items-end">
          <label className="block">
            <span className="block text-xs font-semibold text-gray-600 mb-1">From date</span>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-xl text-sm"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-semibold text-gray-600 mb-1">To date</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-xl text-sm"
            />
          </label>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setFromDate('');
              setToDate('');
            }}
          >
            Clear dates
          </Button>
        </div>

        <form
          onSubmit={saveItem}
          className="bg-white border border-gray-200 rounded-xl p-3 sm:p-4 mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 items-end"
        >
          <label className="block lg:col-span-2">
            <span className="block text-xs font-semibold text-gray-600 mb-1">Name</span>
            <input
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Godown rent / Branch commission"
              className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-semibold text-gray-600 mb-1">Exp type</span>
            <select
              value={form.exp_type}
              onChange={(e) => setForm((prev) => ({ ...prev, exp_type: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm bg-white"
            >
              {EXP_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-semibold text-gray-600 mb-1">Payment due day</span>
            <select
              value={form.payment_due_day}
              onChange={(e) => setForm((prev) => ({ ...prev, payment_due_day: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm bg-white"
            >
              {DUE_DAY_OPTIONS.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-semibold text-gray-600 mb-1">Monthly amount</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.monthly_amount}
              onChange={(e) => setForm((prev) => ({ ...prev, monthly_amount: e.target.value }))}
              placeholder="18000"
              className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-semibold text-gray-600 mb-1">Previous O/S</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.opening_balance}
              onChange={(e) => setForm((prev) => ({ ...prev, opening_balance: e.target.value }))}
              placeholder="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm"
            />
          </label>
          <div className="flex items-center gap-3 xl:col-span-6">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
              />
              Active
            </label>
            <Button type="submit" loading={saving} disabled={saving}>
              {editingId ? 'Update' : 'Save'}
            </Button>
            {editingId ? (
              <Button type="button" variant="secondary" onClick={startCreate}>
                Cancel edit
              </Button>
            ) : null}
          </div>
        </form>

        {loading ? (
          <Loader fullScreen={false} message="Loading payables…" />
        ) : items.length === 0 ? (
          <div className="bg-white border border-dashed border-gray-300 rounded-xl p-8 text-center text-sm text-gray-500">
            No payable items yet. Add rent, EMI, salary or branch commission above.
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="hidden sm:grid grid-cols-12 gap-2 px-4 py-2 text-[10px] uppercase tracking-wide text-gray-500 bg-gray-50">
              <div className="col-span-3">Name</div>
              <div className="col-span-1">Type</div>
              <div className="col-span-1">Due</div>
              <div className="col-span-1">Monthly</div>
              <div className="col-span-1">Per day</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-3 text-right">Action</div>
            </div>
            {items.map((row) => {
              const paid = String(row.payment_status || '').toLowerCase() === 'paid';
              return (
                <div
                  key={row.id}
                  className={`grid grid-cols-1 sm:grid-cols-12 gap-2 px-4 py-3 border-t border-gray-100 text-sm ${
                    editingId === row.id ? 'bg-blue-50' : ''
                  } ${row.is_active ? '' : 'opacity-60'}`}
                >
                  <div className="sm:col-span-3 font-semibold text-gray-800">
                    {row.name}
                    {!row.is_active ? (
                      <span className="ml-2 text-[10px] uppercase text-gray-400">Inactive</span>
                    ) : null}
                    {Number(row.opening_balance || 0) > 0 ? (
                      <p className="text-[11px] font-normal text-gray-500">
                        Prev O/S {money(row.opening_balance)}
                      </p>
                    ) : null}
                  </div>
                  <div className="sm:col-span-1 text-gray-600">{expLabel(row)}</div>
                  <div className="sm:col-span-1 text-gray-600">{row.payment_due_day || '—'}</div>
                  <div className="sm:col-span-1 text-gray-800">{money(row.monthly_amount)}</div>
                  <div className="sm:col-span-1 text-gray-800">
                    {money(Number(row.monthly_amount || 0) / days)}
                  </div>
                  <div className="sm:col-span-2">
                    <button
                      type="button"
                      disabled={togglingId === row.id}
                      onClick={() => togglePayment(row)}
                      className={`px-3 py-1 rounded-lg text-xs font-semibold border transition ${
                        paid
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                          : 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100'
                      }`}
                    >
                      {togglingId === row.id ? '…' : paid ? 'Paid' : 'Pending'}
                    </button>
                    {paid && row.payment_date ? (
                      <p className="text-[10px] text-gray-500 mt-1">{row.payment_date}</p>
                    ) : null}
                  </div>
                  <div className="sm:col-span-3 flex sm:justify-end gap-2">
                    <Button size="sm" variant="secondary" icon={Pencil} onClick={() => startEdit(row)}>
                      Edit
                    </Button>
                    <Button size="sm" variant="danger" icon={Trash2} onClick={() => deleteItem(row)}>
                      Remove
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PlStatutoryPage;
